import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Building2,
  DollarSign,
  Megaphone,
  TrendingUp,
  Scale,
  Lightbulb,
  Cpu,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';

const ServicePackages = () => {
  const [activeCategory, setActiveCategory] = useState('core');

  const serviceCategories = [
    {
      id: 'core',
      name: 'Core Business Setup',
      icon: Building2,
      color: 'from-purple-500 to-purple-600',
      services: [
        { name: 'Business Entity Formation', desc: 'LLC, S-Corp, C-Corp setup with legal filing' },
        { name: 'EIN & Tax Registration', desc: 'Federal and state tax ID registration' },
        { name: 'Registered Agent Service', desc: 'Professional registered agent representation' },
        { name: 'Business Bank Account Setup', desc: 'Banking relationships and account setup' }
      ]
    },
    {
      id: 'financial',
      name: 'Financial Management',
      icon: DollarSign,
      color: 'from-emerald-500 to-teal-600',
      services: [
        { name: 'Business Insurance', desc: 'General liability, E&O, and specialized coverage' },
        { name: 'Licenses & Permits', desc: 'All required local, state, and federal licenses' },
        { name: 'Bookkeeping & Accounting', desc: 'Professional monthly bookkeeping services' },
        { name: 'Payroll Processing', desc: 'Employee payroll and tax withholding' },
        { name: 'Tax Preparation & Filing', desc: 'Annual business tax returns and planning' },
        { name: 'Financial Forecasting', desc: 'Revenue projections and budget planning' },
        { name: 'CFO Advisory Services', desc: 'Strategic financial consulting and analysis' },
        { name: 'Merchant Services', desc: 'Payment processing and POS systems' }
      ]
    },
    {
      id: 'marketing',
      name: 'Marketing & Brand',
      icon: Megaphone,
      color: 'from-violet-500 to-purple-600',
      services: [
        { name: 'Digital Advertising', desc: 'Google Ads, Facebook Ads, LinkedIn campaigns' },
        { name: 'Content Marketing', desc: 'Blog posts, videos, and email campaigns' },
        { name: 'Marketing Automation', desc: 'CRM setup and automated marketing funnels' },
        { name: 'Public Relations', desc: 'Media outreach and press release distribution' },
        { name: 'Graphic Design Services', desc: 'Brand identity and visual assets' }
      ]
    },
    {
      id: 'sales',
      name: 'Sales & Growth',
      icon: TrendingUp,
      color: 'from-amber-500 to-orange-600',
      services: [
        { name: 'Sales Strategy Development', desc: 'Complete sales process and funnel design' },
        { name: 'CRM Implementation', desc: 'Customer relationship management setup' },
        { name: 'Lead Generation Services', desc: 'Outbound and inbound lead generation' },
        { name: 'Sales Training & Coaching', desc: 'Team training and performance optimization' }
      ]
    },
    {
      id: 'legal',
      name: 'Legal & Compliance',
      icon: Scale,
      color: 'from-slate-600 to-slate-700',
      services: [
        { name: 'Contract Review & Drafting', desc: 'Legal agreements and contract management' },
        { name: 'Trademark & IP Protection', desc: 'Intellectual property registration' },
        { name: 'Compliance Management', desc: 'Industry-specific regulatory compliance' },
        { name: 'Employment Law Support', desc: 'HR policies and employment agreements' },
        { name: 'Business Litigation Support', desc: 'Legal representation and dispute resolution' },
        { name: 'Privacy & Data Protection', desc: 'GDPR, CCPA, and privacy compliance' }
      ]
    },
    {
      id: 'advisory',
      name: 'Strategic Advisory',
      icon: Lightbulb,
      color: 'from-blue-500 to-indigo-600',
      services: [
        { name: 'Business Plan Development', desc: 'Comprehensive business planning and strategy' },
        { name: 'Market Research & Analysis', desc: 'Competitive analysis and market validation' },
        { name: 'Funding & Capital Raising', desc: 'Investor pitch decks and fundraising support' },
        { name: 'M&A Advisory', desc: 'Mergers, acquisitions, and exit planning' }
      ]
    },
    {
      id: 'technology',
      name: 'Operations & Tech',
      icon: Cpu,
      color: 'from-cyan-500 to-blue-600',
      services: [
        { name: 'Virtual Office Solutions', desc: 'Phone systems and mail forwarding' },
        { name: 'Project Management', desc: 'Process design and execution management' },
        { name: 'Business Process Automation', desc: 'Workflow automation and optimization' },
        { name: 'IT Infrastructure Setup', desc: 'Cloud services, email, and collaboration tools' },
        { name: 'Software Development', desc: 'Custom software and mobile app development' },
        { name: 'Cybersecurity Services', desc: 'Security audits and data protection' }
      ]
    }
  ];

  const activeServices = serviceCategories.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-800 mb-4">Service Packages</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Comprehensive business services to launch, manage, and scale your company
          </p>
        </div>

        {/* Category Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {serviceCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeCategory === category.id
                  ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-purple-300 hover:shadow'
              }`}
            >
              <category.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{category.name}</span>
            </button>
          ))}
        </div>

        {/* Active Category Header */}
        {activeServices && (
          <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-slate-800 to-slate-900 text-white">
            <CardContent className="p-6 flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${activeServices.color} flex items-center justify-center`}>
                <activeServices.icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{activeServices.name}</h2>
                <p className="text-slate-300">{activeServices.services.length} services available</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Services Grid */}
        {activeServices && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeServices.services.map((service, idx) => (
              <Card key={idx} className="border border-slate-200 hover:border-purple-300 hover:shadow-lg transition-all cursor-pointer group">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 mb-1 group-hover:text-purple-600 transition-colors">
                        {service.name}
                      </h3>
                      <p className="text-sm text-slate-500">{service.desc}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-purple-500 transition-colors" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-600 mb-4">Need help choosing the right services?</p>
          <Button className="bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 text-white px-8 py-6 text-lg rounded-xl">
            Start AI Interview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServicePackages;
